import React, { useEffect, useMemo, useState } from "react";
import { FaDatabase, FaSearch, FaTable, FaArrowRight, FaInfoCircle } from "react-icons/fa";
import {
  getCensusMunicipalitySummary,
  getCensusSector,
  getCensusVariables,
  listCensusSectors,
} from "../lib/api/market-intelligence-census.functions";
import { censusLookupCopy } from "../lib/i18n/censusLookupCopy";
import "./CensusIntelligenceLookup.css";

const DEFAULT_MUNICIPALITY = "3550308";
const DEFAULT_SECTOR = "350010505000001";
const SEMANTIC_GROUPS = [
  { value: "", label: "Todos" },
  { value: "population_total", label: "population_total" },
  { value: "households", label: "households" },
  { value: "occupancy", label: "occupancy" },
  { value: "age_or_demographic_structure", label: "age_or_demographic_structure" },
  { value: "gender", label: "gender" },
];

function formatNumber(value, options = {}) {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat("pt-BR", options).format(numeric);
}

function formatBool(value) {
  if (value === true) return "Sim";
  if (value === false) return "Não";
  return "—";
}

function errorMessage(error, fallback) {
  if (error?.status === 404) return "Nenhum resultado encontrado para o código informado.";
  return error?.message || fallback;
}

function StatCard({ label, value, detail }) {
  return (
    <div className="census-stat">
      <span>{label}</span>
      <strong>{value}</strong>
      {detail && <small>{detail}</small>}
    </div>
  );
}

function FieldRow({ label, value }) {
  return (
    <div className="census-field-row">
      <span>{label}</span>
      <strong>{value ?? "—"}</strong>
    </div>
  );
}

function EmptyState({ title, detail }) {
  return (
    <div className="census-empty">
      <FaInfoCircle />
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  );
}

function LoadingBar({ active }) {
  return active ? <div className="census-loading" aria-label="Carregando" /> : null;
}

function CensusIntelligenceLookup() {
  const copy = censusLookupCopy.pt;
  const [activeTab, setActiveTab] = useState("municipality");

  const [municipalityCode, setMunicipalityCode] = useState(DEFAULT_MUNICIPALITY);
  const [municipalitySummary, setMunicipalitySummary] = useState(null);
  const [municipalitySectors, setMunicipalitySectors] = useState([]);
  const [municipalityCursor, setMunicipalityCursor] = useState(null);
  const [municipalityHistory, setMunicipalityHistory] = useState([]);
  const [municipalityLoading, setMunicipalityLoading] = useState(false);
  const [municipalityError, setMunicipalityError] = useState("");

  const [sectorCode, setSectorCode] = useState(DEFAULT_SECTOR);
  const [includeDemographics, setIncludeDemographics] = useState(false);
  const [sector, setSector] = useState(null);
  const [sectorLoading, setSectorLoading] = useState(false);
  const [sectorError, setSectorError] = useState("");

  const [semanticGroup, setSemanticGroup] = useState("");
  const [variables, setVariables] = useState([]);
  const [variablesLoading, setVariablesLoading] = useState(false);
  const [variablesError, setVariablesError] = useState("");

  const sectorDemographics = useMemo(() => {
    if (!sector) return [];
    return Object.entries(sector)
      .filter(([key]) => /^v010(0[6-9]|[1-3][0-9]|4[01])$/.test(key))
      .map(([key, value]) => ({ key, value }));
  }, [sector]);

  async function loadMunicipality(cursor = null, pushHistory = false) {
    setMunicipalityError("");
    const code = municipalityCode.trim();
    if (!/^\d{7}$/.test(code)) {
      setMunicipalityError("Informe um código municipal IBGE com 7 dígitos.");
      return;
    }
    setMunicipalityLoading(true);
    try {
      const [summary, page] = await Promise.all([
        getCensusMunicipalitySummary(code),
        listCensusSectors({ cd_mun: code, limit: 100, cursor }),
      ]);
      setMunicipalitySummary(summary);
      setMunicipalitySectors(page.items);
      setMunicipalityCursor(page.next_cursor);
      if (pushHistory) setMunicipalityHistory((history) => [...history, cursor]);
      if (!cursor) setMunicipalityHistory([]);
    } catch (error) {
      setMunicipalitySummary(null);
      setMunicipalitySectors([]);
      setMunicipalityCursor(null);
      setMunicipalityError(errorMessage(error, "Não foi possível carregar o resumo municipal."));
    } finally {
      setMunicipalityLoading(false);
    }
  }

  async function loadSector() {
    setSectorError("");
    const code = sectorCode.trim();
    if (!/^\d{15}$/.test(code)) {
      setSectorError("Informe um código de setor censitário com 15 dígitos.");
      return;
    }
    setSectorLoading(true);
    try {
      const include = includeDemographics ? ["demographics", "derived"] : ["derived"];
      setSector(await getCensusSector(code, include));
    } catch (error) {
      setSector(null);
      setSectorError(errorMessage(error, "Não foi possível carregar o setor censitário."));
    } finally {
      setSectorLoading(false);
    }
  }

  async function loadVariables(group = semanticGroup) {
    setVariablesError("");
    setVariablesLoading(true);
    try {
      setVariables(await getCensusVariables(group));
    } catch (error) {
      setVariables([]);
      setVariablesError(errorMessage(error, "Não foi possível carregar o dicionário."));
    } finally {
      setVariablesLoading(false);
    }
  }

  useEffect(() => {
    loadMunicipality();
    loadSector();
    loadVariables("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleVariableGroupChange(event) {
    const group = event.target.value;
    setSemanticGroup(group);
    loadVariables(group);
  }

  function handlePreviousMunicipalityPage() {
    if (!municipalityHistory.length) return;
    const nextHistory = municipalityHistory.slice(0, -1);
    const previousCursor = nextHistory[nextHistory.length - 1] || null;
    setMunicipalityHistory(nextHistory);
    loadMunicipality(previousCursor, false);
  }

  return (
    <main className="census-page">
      <section className="census-header">
        <div>
          <div className="census-kicker">
            <FaDatabase />
            <span>{copy.kicker}</span>
          </div>
          <h1>{copy.title}</h1>
          <p>{copy.subtitle}</p>
        </div>
      </section>

      <section className="census-safety">
        <FaInfoCircle />
        <span>{copy.safety}</span>
      </section>

      <div className="census-tabs" role="tablist" aria-label="Modos de consulta censitária">
        <button className={activeTab === "municipality" ? "active" : ""} onClick={() => setActiveTab("municipality")}>
          <FaSearch /> {copy.tabs.municipality}
        </button>
        <button className={activeTab === "sector" ? "active" : ""} onClick={() => setActiveTab("sector")}>
          <FaDatabase /> {copy.tabs.sector}
        </button>
        <button className={activeTab === "variables" ? "active" : ""} onClick={() => setActiveTab("variables")}>
          <FaTable /> {copy.tabs.variables}
        </button>
      </div>

      {activeTab === "municipality" && (
        <section className="census-panel">
          <div className="census-panel-head">
            <div>
              <h2>Resumo municipal</h2>
              <p>Use o código IBGE do município. Exemplo: 3550308 para São Paulo.</p>
            </div>
            <form
              className="census-form"
              onSubmit={(event) => {
                event.preventDefault();
                loadMunicipality();
              }}
            >
              <input
                value={municipalityCode}
                onChange={(event) => setMunicipalityCode(event.target.value)}
                inputMode="numeric"
                maxLength={7}
                aria-label="Código IBGE municipal"
              />
              <button type="submit" disabled={municipalityLoading}>
                <FaSearch /> Consultar
              </button>
            </form>
          </div>

          <LoadingBar active={municipalityLoading} />
          {municipalityError && <EmptyState title="Consulta municipal indisponível" detail={municipalityError} />}

          {municipalitySummary && (
            <>
              <div className="census-stat-grid">
                <StatCard label="Município" value={municipalitySummary.nm_mun} detail={municipalitySummary.cd_mun} />
                <StatCard label="UF" value={municipalitySummary.nm_uf} detail={municipalitySummary.cd_uf} />
                <StatCard label="Setores" value={formatNumber(municipalitySummary.sector_count)} />
                <StatCard label="População total" value={formatNumber(municipalitySummary.total_population)} detail="v0001" />
                <StatCard label="Área total km²" value={formatNumber(municipalitySummary.total_area_km2, { maximumFractionDigits: 2 })} />
                <StatCard label="Densidade" value={formatNumber(municipalitySummary.population_density_per_km2, { maximumFractionDigits: 2 })} detail="hab/km²" />
                <StatCard label="Cobertura demográfica" value={`${formatNumber(municipalitySummary.demographics?.coverage_pct, { maximumFractionDigits: 4 })}%`} detail={`${formatNumber(municipalitySummary.demographics?.covered_count)} cobertos`} />
                <StatCard label="Geometria" value={municipalitySummary.geometry?.available ? "Disponível" : "Indisponível"} detail={municipalitySummary.geometry?.uf || "sem camada"} />
              </div>

              <div className="census-notes">
                {(municipalitySummary.missing_data_notes || []).map((note) => (
                  <span key={note}>{note}</span>
                ))}
                <span>Esta tela não gera scores, rankings, avaliações imobiliárias ou recomendações de investimento.</span>
              </div>
            </>
          )}

          <div className="census-table-head">
            <h3>Setores censitários</h3>
            <div className="census-pagination">
              <button type="button" onClick={handlePreviousMunicipalityPage} disabled={!municipalityHistory.length || municipalityLoading}>
                Página anterior
              </button>
              <button type="button" onClick={() => loadMunicipality(municipalityCursor, true)} disabled={!municipalityCursor || municipalityLoading}>
                Próxima página <FaArrowRight />
              </button>
            </div>
          </div>
          <div className="census-table-wrap">
            <table className="census-table">
              <thead>
                <tr>
                  <th>cd_setor</th>
                  <th>nm_bairro</th>
                  <th>area_km2</th>
                  <th>v0001</th>
                  <th>densidade</th>
                  <th>demografia</th>
                  <th>geom. UF</th>
                </tr>
              </thead>
              <tbody>
                {municipalitySectors.map((item) => (
                  <tr key={item.cd_setor}>
                    <td>{item.cd_setor}</td>
                    <td>{item.nm_bairro || "—"}</td>
                    <td>{formatNumber(item.area_km2, { maximumFractionDigits: 4 })}</td>
                    <td>{formatNumber(item.v0001)}</td>
                    <td>{formatNumber(item.population_density_per_km2, { maximumFractionDigits: 2 })}</td>
                    <td>{formatBool(item.has_demographics)}</td>
                    <td>{item.geometry_uf_available || "—"}</td>
                  </tr>
                ))}
                {!municipalitySectors.length && !municipalityLoading && (
                  <tr>
                    <td colSpan="7">Nenhum setor carregado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activeTab === "sector" && (
        <section className="census-panel">
          <div className="census-panel-head">
            <div>
              <h2>Setor censitário</h2>
              <p>Consulta por código exato de setor. Exemplo: 350010505000001.</p>
            </div>
            <form
              className="census-form"
              onSubmit={(event) => {
                event.preventDefault();
                loadSector();
              }}
            >
              <input
                value={sectorCode}
                onChange={(event) => setSectorCode(event.target.value)}
                inputMode="numeric"
                maxLength={15}
                aria-label="Código do setor censitário"
              />
              <label className="census-toggle">
                <input
                  type="checkbox"
                  checked={includeDemographics}
                  onChange={(event) => setIncludeDemographics(event.target.checked)}
                />
                Incluir demografia
              </label>
              <button type="submit" disabled={sectorLoading}>
                <FaSearch /> Consultar
              </button>
            </form>
          </div>

          <LoadingBar active={sectorLoading} />
          {sectorError && <EmptyState title="Setor não encontrado" detail={sectorError} />}

          {sector && (
            <div className="census-sector-layout">
              <div className="census-field-panel">
                <h3>Identidade</h3>
                <FieldRow label="cd_setor" value={sector.cd_setor} />
                <FieldRow label="Município" value={`${sector.nm_mun || "—"} (${sector.cd_mun || "—"})`} />
                <FieldRow label="UF" value={`${sector.nm_uf || "—"} (${sector.cd_uf || "—"})`} />
                <FieldRow label="Bairro" value={sector.nm_bairro || sector.cd_bairro || "—"} />
              </div>
              <div className="census-field-panel">
                <h3>Indicadores</h3>
                <FieldRow label="Área km²" value={formatNumber(sector.area_km2, { maximumFractionDigits: 4 })} />
                <FieldRow label="População v0001" value={formatNumber(sector.v0001)} />
                <FieldRow label="Densidade" value={formatNumber(sector.population_density_per_km2, { maximumFractionDigits: 2 })} />
                <FieldRow label="Média moradores/domicílio" value={formatNumber(sector.avg_residents_per_household, { maximumFractionDigits: 4 })} />
                <FieldRow label="Participação masculina" value={formatNumber(sector.male_share, { style: "percent", maximumFractionDigits: 2 })} />
                <FieldRow label="Participação feminina" value={formatNumber(sector.female_share, { style: "percent", maximumFractionDigits: 2 })} />
              </div>
              <div className="census-field-panel">
                <h3>Disponibilidade</h3>
                <FieldRow label="has_demographics" value={formatBool(sector.has_demographics)} />
                <FieldRow label="has_geometry_sp" value={formatBool(sector.has_geometry_sp)} />
                <FieldRow label="has_geometry_pr" value={formatBool(sector.has_geometry_pr)} />
                <FieldRow label="has_geometry_mg" value={formatBool(sector.has_geometry_mg)} />
                <FieldRow label="has_geometry_rs" value={formatBool(sector.has_geometry_rs)} />
                <FieldRow label="has_geometry_rj" value={formatBool(sector.has_geometry_rj)} />
                <FieldRow label="geometry_uf_available" value={sector.geometry_uf_available || "—"} />
              </div>
            </div>
          )}

          {includeDemographics && sectorDemographics.length > 0 && (
            <details className="census-details">
              <summary>Variáveis demográficas avançadas</summary>
              <div className="census-variable-grid">
                {sectorDemographics.map((item) => (
                  <FieldRow key={item.key} label={item.key} value={formatNumber(item.value)} />
                ))}
              </div>
            </details>
          )}
        </section>
      )}

      {activeTab === "variables" && (
        <section className="census-panel">
          <div className="census-panel-head">
            <div>
              <h2>Dicionário de variáveis</h2>
              <p>Metadados semânticos das variáveis censitárias atualmente publicadas.</p>
            </div>
            <div className="census-form">
              <select value={semanticGroup} onChange={handleVariableGroupChange} aria-label="Grupo semântico">
                {SEMANTIC_GROUPS.map((group) => (
                  <option value={group.value} key={group.value || "all"}>
                    {group.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <LoadingBar active={variablesLoading} />
          {variablesError && <EmptyState title="Dicionário indisponível" detail={variablesError} />}

          <div className="census-table-head">
            <h3>{formatNumber(variables.length)} variáveis</h3>
          </div>
          <div className="census-table-wrap">
            <table className="census-table">
              <thead>
                <tr>
                  <th>variable_code</th>
                  <th>label</th>
                  <th>semantic_group</th>
                  <th>unit</th>
                  <th>value_type</th>
                </tr>
              </thead>
              <tbody>
                {variables.map((item) => (
                  <tr key={item.variable_code}>
                    <td>{item.variable_code}</td>
                    <td>{item.label}</td>
                    <td>{item.semantic_group}</td>
                    <td>{item.unit}</td>
                    <td>{item.value_type}</td>
                  </tr>
                ))}
                {!variables.length && !variablesLoading && (
                  <tr>
                    <td colSpan="5">Nenhuma variável encontrada para o filtro.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </main>
  );
}

export default CensusIntelligenceLookup;
